import type {CurrentAuthService} from 'shared/services';
import type {FondPermissions, FondPermissionsDataService} from 'src/features/erp';
import type {Observable} from 'rxjs';
import {BehaviorSubject, map} from 'rxjs';
import {exists} from 'shared/utils/custom-utils';

interface FondPermissionsState extends FondPermissions {
    initialized: boolean;
}

export class ErpFondPermissionsContextService {
    public static serviceName = 'erpFondPermissionsContextService';

    private readonly fondPermissions: BehaviorSubject<FondPermissionsState>;

    /*@ngInject*/
    constructor(private currentAuthService: CurrentAuthService,
                private fondPermissionsDataService: FondPermissionsDataService) {

        this.fondPermissions = new BehaviorSubject<FondPermissionsState>({
            showable: [],
            editable: [],
            initialized: false
        });
    }

    public setup(): void {
        this.currentAuthService.currentAuth$().subscribe((currentAuth) => {
            if (!currentAuth.evided) {
                this.fondPermissions.next({showable: [], editable: [], initialized: true});
                return;
            }

            this.fondPermissions.next({showable: [], editable: [], initialized: false});
            this.loadUserFondPermissions();
        });
    }

    public hasShowPermission$(fondId: number): Observable<boolean> {
        return this.fondPermissions.pipe(
            map((permissions) => permissions.showable.includes(fondId))
        );
    }

    public hasEditPermission$(fondId: number): Observable<boolean> {
        return this.fondPermissions.pipe(
            map((permissions) => permissions.editable.includes(fondId))
        );
    }

    public hasShowFondPermissions(permissions: FondPermissions, fondId: number | null | undefined): boolean {
        if (!exists(fondId)) {
            return true;
        }

        return permissions.showable.includes(fondId);
    }

    public hasEditFondPermissions(permissions: FondPermissions, fondId: number | null | undefined): boolean {
        if (!exists(fondId)) {
            return true;
        }

        return permissions.editable.includes(fondId);
    }

    public hasFondPermissions(permissions: FondPermissions, showFondId: number | null, editFondId: number | null): boolean {
        if (!exists(showFondId) && !exists(editFondId)) {
            return true;
        }

        if (exists(showFondId) && exists(editFondId)) {
            return this.hasShowFondPermissions(permissions, showFondId) && this.hasEditFondPermissions(permissions, editFondId);
        }

        if (exists(editFondId)) {
            return this.hasEditFondPermissions(permissions, editFondId);
        }

        if (exists(showFondId)) {
            return this.hasShowFondPermissions(permissions, showFondId);
        }

        return false;
    }

    public getFondPermissionsValue(): FondPermissionsState {
        return this.fondPermissions.getValue();
    }

    public get fondPermissions$(): Observable<FondPermissionsState> {
        return this.fondPermissions.asObservable();
    }

    private async loadUserFondPermissions(): Promise<void> {
        try {
            const fondPermissions = await this.fondPermissionsDataService.getUserFondPermissions();
            this.fondPermissions.next({...fondPermissions, initialized: true});
        } catch {
            this.fondPermissions.next({showable: [], editable: [], initialized: true});
        }
    }
}